import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/Alert';

/**
 * Generic OAuth-callback landing page for the device layer
 * (/auth/:provider/callback). The backend redirects here with ?success=true or
 * ?error=<code>. The static /auth/fitbit/callback route still wins for Fitbit.
 */
const DeviceCallbackPage = () => {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [processing, setProcessing] = useState(true);

  const label = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'Device';

  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (success === 'true') {
      setMessage(`${label} connected successfully!`);
      setIsSuccess(true);
    } else if (error) {
      const errorMessages = {
        access_denied: `${label} connection was cancelled. Please try again if you want to connect your account.`,
        server_config: 'Server configuration error. Please contact support.',
        missing_state: 'Security validation failed. Please try again.',
        invalid_state: 'Invalid authentication state. Please try again.',
        missing_code: 'No authorization code was returned. Please try again.',
        user_not_found: 'User not found. Please log in and try again.',
        token_exchange_failed: `Failed to complete the ${label} connection. Please try again.`,
        network_error: 'Network error. Please check your connection and try again.',
        server_error: 'Server error. Please try again later.',
      };
      setMessage(errorMessages[error] || `An error occurred while connecting your ${label} account. Please try again.`);
      setIsSuccess(false);
    } else {
      setMessage(`Unknown response from ${label}. Please try again.`);
      setIsSuccess(false);
    }

    setProcessing(false);
    const timer = setTimeout(() => navigate('/me'), 2000);
    return () => clearTimeout(timer);
  }, [searchParams, navigate, label]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {processing ? (
            <>
              <div className="flex justify-center mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Processing {label} Connection...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we complete the connection.
              </p>
            </>
          ) : (
            <>
              {message && (
                <Alert variant={isSuccess ? 'default' : 'destructive'} className="mb-4">
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
              <p className="text-gray-600 dark:text-gray-400">
                Redirecting you back to your profile...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceCallbackPage;
